import classNames from "classnames";
import { useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { ImpactCategory } from "../../../domain/impactCategories";
import { LinkButton } from "../../utils/Button";
import DropDownToggle from "../../utils/DropDownToggle";
import InlineTitleToggle from "../../utils/InlineTitleToggle";
import { useImpactCategory } from "../ImpactCategoryProvider";

import "./ChartsContainer.css";

interface ChartsOverTimeControllerProps {
  handleChangeChart: (chart: string) => void;
  handleChangePeriod: (period: string) => void;
  selectedChart: string;
  availableCharts: string[];
  availablePeriods: { label: string; value: string }[];
  selectedPeriod: string;
  selectedYear: { id: string; label: string };
  availableYears: { id: string; label: string }[];
  handleChangeYear: (e: { label: string; id: string }) => void;
}

const ChartsOverTimeController: React.FC<ChartsOverTimeControllerProps> = ({
  handleChangeChart,
  handleChangePeriod,
  selectedChart,
  availableCharts,
  availablePeriods,
  selectedPeriod,
  availableYears,
  selectedYear,
  handleChangeYear,
}) => {
  const { impactCategory } = useImpactCategory();

  const intl = useIntl();

  const onChange = useCallback(
    (e) => {
      handleChangeChart(e);
    },
    [handleChangeChart]
  );

  const renderCharts = (chart: string) => {
    if (chart === "IMPACT_CATEGORY") {
      if (impactCategory === ImpactCategory.GHG) {
        return intl.formatMessage({
          defaultMessage: "GHG Emissions",
          id: "domain/EffectType:ghg/title",
        });
      } else if (impactCategory === ImpactCategory.LAND_USE) {
        return intl.formatMessage({
          defaultMessage: "Land Use",
          id: "domain/EffectType:landUse/title",
        });
      } else if (impactCategory === ImpactCategory.WATER_USE) {
        return intl.formatMessage({
          defaultMessage: "Water Use",
          id: "domain/EffectType:waterUse/title",
        });
      }
    }
    if (chart === "RATING_DISTRIBUTION") {
      return intl.formatMessage({
        defaultMessage: "Sales by Rating",
        id: "components/pages:SalesDashBoard/ratingDistributionChart/title",
      });
    }
  };

  const handlePeriodChange = useCallback(
    (period) => {
      handleChangePeriod(period);
    },
    [handleChangePeriod]
  );

  return (
    <div
      style={{
        display: "flex",
        height: "fit-content",
        alignItems: "center",
        marginBottom: "1rem",
      }}
    >
      <div
        style={{
          display: "flex",
          textAlign: "center",
          height: "fit-content",
          alignItems: "center",
          gap: "8px",
          flexGrow: 1,
        }}
      >
        <InlineTitleToggle
          onChange={onChange}
          options={availableCharts}
          selectedOption={selectedChart}
          renderOption={renderCharts}
        />{" "}
        <h3 className="ChartsContainer_title_overtime mb-0">
          <FormattedMessage
            id="components/salesDashboard/chartsOverTime:title"
            defaultMessage="Over Time"
          />
        </h3>
      </div>
      <div className="d-flex" style={{ alignItems: "center", gap: "1rem" }}>
        {selectedPeriod !== "YEARLY" && (
          <DropDownToggle<{ id: string; label: string }>
            dropDownClassname="charts-years-dropwdown"
            dropDownMenuClassname="charts-years-dropwdown-menu"
            onChange={handleChangeYear}
            options={availableYears}
            selectedOption={selectedYear || ""}
            renderOption={(y) => y.label}
          />
        )}
        {availablePeriods.length > 1 ? (
          <div className="ChartsContainer_period_options d-flex">
            {availablePeriods.map((period) => {
              return (
                <LinkButton
                  key={period.value}
                  className={classNames(
                    "ChartsContainer_period_option small-copy-medium",
                    {
                      ChartsContainer_period_option_selected:
                        selectedPeriod === period.value,
                    }
                  )}
                  onClick={() => handlePeriodChange(period.value)}
                >
                  {period.label}
                </LinkButton>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ChartsOverTimeController;
